(() => {
  const selectors = {
    link: '[data-section-link]',
    paginationLink: '[data-section-pagination] a',
    productForm: 'form[data-product-form][action*="/cart/add"]',
    renderer: '[data-section-renderer]',
    form: '[data-filter-form]',
    sort: '[data-filter-sort]',
    status: '[data-section-render-status]',
  };

  const sectionRenderState = new WeakMap();
  let sectionRenderCounter = 0;

  function cleanUrlParams(searchParams) {
    Array.from(searchParams.entries()).forEach(([key, value]) => {
      if (value === '' || key === 'page' || key === 'sections') {
        searchParams.delete(key);
      }
    });
  }

  function buildFormUrl(form) {
    const url = new URL(
      form.action || window.location.href,
      window.location.origin,
    );
    const params = new URLSearchParams(new FormData(form));
    cleanUrlParams(params);
    url.search = params.toString();
    return url;
  }

  function getRenderer(node) {
    return node.closest(selectors.renderer);
  }

  function focusSectionRenderStatus(renderer) {
    const status = renderer.querySelector(selectors.status);
    if (!(status instanceof HTMLElement)) return;
    if (!status.hasAttribute('tabindex')) {
      status.setAttribute('tabindex', '-1');
    }
    window.setTimeout(() => {
      status.focus({ preventScroll: true });
    }, 0);
  }

  async function fetchRenderedSectionHtml(url, sectionId, signal) {
    const jsonUrl = new URL(url.toString(), window.location.origin);
    jsonUrl.searchParams.set('sections', sectionId);

    const jsonResponse = await fetch(jsonUrl.toString(), {
      headers: { Accept: 'application/json' },
      signal,
    });

    if (jsonResponse.ok) {
      const contentType = jsonResponse.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const sections = await jsonResponse.json().catch(() => ({}));
        if (sections[sectionId]) return sections[sectionId];
      }
    }

    const htmlUrl = new URL(url.toString(), window.location.origin);
    htmlUrl.searchParams.set('section_id', sectionId);
    htmlUrl.searchParams.delete('sections');

    const htmlResponse = await fetch(htmlUrl.toString(), {
      headers: { Accept: 'text/html' },
      signal,
    });
    if (!htmlResponse.ok) throw new Error('Section render failed');

    return htmlResponse.text();
  }

  async function renderSection(url, renderer, options = {}) {
    if (!renderer?.dataset.sectionId) {
      window.location.assign(url.toString());
      return;
    }

    const previous = sectionRenderState.get(renderer);
    previous?.controller?.abort();

    const controller = new AbortController();
    const token = `${Date.now()}-${(sectionRenderCounter += 1)}`;
    sectionRenderState.set(renderer, { controller, token });
    renderer.setAttribute('aria-busy', 'true');

    try {
      const html = await fetchRenderedSectionHtml(
        url,
        renderer.dataset.sectionId,
        controller.signal,
      );
      if (sectionRenderState.get(renderer)?.token !== token) return;
      if (!html) throw new Error('Rendered section missing');

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next =
        parsed.querySelector(
          `${selectors.renderer}[data-section-id="${renderer.dataset.sectionId}"]`,
        ) || parsed.querySelector(selectors.renderer);
      if (!next) throw new Error('Rendered section root missing');

      renderer.dispatchEvent(
        new CustomEvent('shopify:section:unload', { bubbles: true }),
      );
      renderer.replaceWith(next);
      next.dispatchEvent(
        new CustomEvent('shopify:section:load', { bubbles: true }),
      );
      if (options.focusStatus !== false) {
        focusSectionRenderStatus(next);
      }
      if (options.pushState !== false) {
        window.history.pushState({}, '', url.toString());
      }
      window.dispatchEvent(
        new CustomEvent('shopify:section-rendered', {
          detail: { sectionId: renderer.dataset.sectionId },
        }),
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      throw error;
    } finally {
      if (sectionRenderState.get(renderer)?.token === token) {
        sectionRenderState.delete(renderer);
        renderer.removeAttribute('aria-busy');
      }
    }
  }

  function submitFormNatively(form) {
    HTMLFormElement.prototype.submit.call(form);
  }

  function submitSectionForm(form) {
    const renderer = getRenderer(form);
    const url = buildFormUrl(form);
    renderSection(url, renderer).catch(() => submitFormNatively(form));
  }

  function restoreRenderedSectionsFromLocation() {
    const renderers = Array.from(document.querySelectorAll(selectors.renderer));
    if (renderers.length === 0) return;

    const url = new URL(window.location.href);
    renderers.forEach((renderer) => {
      renderSection(url, renderer, {
        focusStatus: false,
        pushState: false,
      }).catch(() => {
        window.location.reload();
      });
    });
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const sectionLink = event.target.closest(
      `${selectors.paginationLink}, ${selectors.link}`,
    );
    if (!sectionLink) return;

    const renderer = getRenderer(sectionLink);
    if (!renderer) return;

    event.preventDefault();
    renderSection(new URL(sectionLink.href, window.location.origin), renderer).catch(
      () => {
        window.location.assign(sectionLink.href);
      },
    );
  });

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest(selectors.sort)) {
      const form = event.target.closest(selectors.form);
      if (form) submitSectionForm(form);
      return;
    }

    if (event.target.matches(`${selectors.form} input[type="checkbox"]`)) {
      const form = event.target.closest(selectors.form);
      if (form) submitSectionForm(form);
    }
  });

  document.addEventListener('submit', (event) => {
    if (!(event.target instanceof Element)) return;

    const sectionForm = event.target.closest(selectors.form);
    if (sectionForm && !event.target.matches(selectors.productForm)) {
      event.preventDefault();
      submitSectionForm(sectionForm);
    }
  });

  window.addEventListener('popstate', restoreRenderedSectionsFromLocation);

  window.MedistikCollectionRenderer = {
    renderSection,
    submitSectionForm,
  };
})();
